import SidebarLogo from "@/assets/CoopUnionIcon.svg";
import { AppColors } from "@/styles/colors";
import { CustomAccordion } from "@/components/accordion/CustomAccordion";
import { useState } from "react";
import { Accordion, Box, Flex, Image } from "@chakra-ui/react";
import { UserInfo } from "../section-components/UserInfo";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { PermissionCode } from "@/constants/permissions";
import { OnboardingView } from "./components/OnboardingView";
import { PersonsView } from "./components/PersonsView";
import { RolesView } from "./components/RolesView";
import { RequestsView } from "./components/RequestsView";
import { useRoles } from "@/hooks/useRoles";
import { LinearProgress } from "@/components/progress";
import { AccountsView } from "./components/AccountsView";
import { v4 } from "uuid";
import { ConfigView } from "./components/ConfigView";
import { GrainRequestsView } from "./components/GrainRequestsView";
import { SuppliesView } from "./components/SuppliesView";
import { ATAProdView } from "./components/ATAProdView";
import { RSATyGProdView } from "./components/RSATyGProdView";
import { ProductionMasterView } from "./components/ProductionMasterView";

export const Sidebar = () => {
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  const { loadingPermissions, userPermissions } = useRoles();

  const navigate = useNavigate();

  const sidebarItems = [
    {
      value: "ONBOARDING",
      title: "Onboarding",
      content: <OnboardingView permissions={userPermissions ?? []} />,
      route: AppRoutes.ONBOARDING,
      permissionCodes: [PermissionCode.CONTACT_REQUESTS, PermissionCode.USERS],
    },
    {
      value: "PERSONS",
      title: "Personas",
      content: <PersonsView permissions={userPermissions ?? []} />,
      route: AppRoutes.PERSONS,
      permissionCodes: [PermissionCode.PERSONS],
    },
    {
      value: "ACCOUNTS",
      title: "Cuentas",
      content: <AccountsView permissions={userPermissions || []} />,
      route: AppRoutes.ACCOUNTS,
      permissionCodes: [PermissionCode.ACCOUNTS, PermissionCode.CBU],
    },
    {
      value: "ROLES",
      title: "Administración de roles",
      content: <RolesView permissions={userPermissions || []} />,
      route: AppRoutes.ROLES,
      permissionCodes: [
        PermissionCode.ROLES_PERMISSIONS,
        PermissionCode.ROLE_ASSIGNATION,
      ],
    },
    {
      value: "REQUESTS",
      title: "Solicitudes financieras",
      content: <RequestsView permissions={userPermissions || []} />,
      permissionCodes: [
        PermissionCode.CASH_REQUEST,
        PermissionCode.CHECK_REQUEST,
        PermissionCode.TRANSFER_REQUEST,
        PermissionCode.SCHEDULED_TRANSFER,
      ],
    },
    {
      value: "GRAIN_REQUESTS",
      title: "Solicitudes granarias",
      content: <GrainRequestsView permissions={userPermissions ?? []} />,
      permissionCodes: [PermissionCode.GRAIN_SELL_ORDER, PermissionCode.COUNTER_DELIVERY, PermissionCode.GRAIN_FUTURE_CONTRACT],
    },
    {
      value: "SUPPLIES",
      title: "Solicitudes insumos",
      content: <SuppliesView permissions={userPermissions ?? []} />,
      permissionCodes: [PermissionCode.FERT_SLOTS_REQ],
    },
      {
      value: "RSATYG_PRODUCTION",
      title: "Producción RSATyG",
      content: <RSATyGProdView permissions={userPermissions ?? []} />,
      permissionCodes: [PermissionCode.SAMPLING_REQUESTS],
    },
    {
      value: "ATA_PRODUCTION",
      title: "Producción ATA",
      content: <ATAProdView permissions={userPermissions ?? []} />,
      permissionCodes: [PermissionCode.FIELD_VISIT],
    },
    {
      value: "PRODUCTION_MASTER",
      title: "Maestros Producción",
      content: <ProductionMasterView permissions={userPermissions ?? []} />,
      permissionCodes: [PermissionCode.PRODUCTION_GRAINS],
    },
    {
      value: "CONFIG",
      title: "Configuración",
      content: <ConfigView />,
      permissionCodes: [
        PermissionCode.MARKET_CONFIG,
        PermissionCode.BUSINESS_POLICY,
        PermissionCode.MOTIVES,
        PermissionCode.PRODUCTS,
        PermissionCode.TRUCKS,
        PermissionCode.DRIVERS,
        PermissionCode.TECHNICIANS,
        PermissionCode.LOGISTIC,
        PermissionCode.FAMILY_RELATIONS_FIELDS,
        PermissionCode.FINANCIAL_MOVEMENTS_DESC,
      ],
    },
  ];

  const hasReadPermission = (codes: string[]) =>
    // Función que valida si el usuario tiene permiso de lectura (tipo "R") sobre alguno de los códigos del grupo
    userPermissions?.some(
      (up) => codes.includes(up.codigo) && up.tipoPermiso === "R",
    );

  const visibleSidebarItems = sidebarItems.filter(
    (item) =>
      item.permissionCodes.length > 0 &&
      hasReadPermission(item.permissionCodes),
  );

  return (
    <Flex padding={2} height="100%">
      <Flex
        flexDir="column"
        bgColor={AppColors.WHITE_SECONDARY}
        paddingLeft="10px"
        paddingY="15px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        borderRadius="8px"
        alignItems="center"
        justifyContent="flex-start"
        minW="300px"
        height="100%"
        position="sticky"
        top="0"
        padding={{ base: "0", md: "10px", "2xl": "10px" }}
        data-testid="sidebar"
      >
        <Image
          src={SidebarLogo}
          marginY="25px"
          onClick={() => navigate(AppRoutes.HOME)}
          cursor="pointer"
          width={100}
          height={100}
        />
        <Box
          w="100%"
          borderBottom={`1px solid ${AppColors.WHITE_BORDER_1}`}
          my={4}
        />
        {loadingPermissions && (
          <Box width="100%">
            <LinearProgress />
          </Box>
        )}
        {/* Contenedor ajustable con scroll según breakpoint */}
        <Flex
          flex="1"
          w="100%"
          overflowY="auto"
          flexDir="column"
          minH="0"
          paddingBottom="10px"
        >
          {visibleSidebarItems?.map((item) => {
            return (
              <Accordion.Root
                key={v4()}
                collapsible
                value={accordionValue}
                onValueChange={(e) => setAccordionValue(e.value)}
                variant="subtle"
                w="100%"
              >
                <CustomAccordion
                  key={item.value}
                  title={item.title}
                  value={item.value}
                  content={item.content}
                  variant="subtle"
                />
              </Accordion.Root>
            );
          })}
        </Flex>

        {/* Sección fija para el usuario */}
        <Flex w="100%" mt="auto">
          <UserInfo />
        </Flex>
      </Flex>
    </Flex>
  );
};
